// usePeekLeadingPills.js — database pill for Peek metadata footer

import { useCallback, useMemo } from 'react';

import { useBase } from 'context/BaseContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';

import useDatabaseOpenIntent from 'views/hooks/useDatabaseOpenIntent';
import { getNodeTypeLabel } from 'utilities/NodeTypeLabels';

/**
 * Builds leading metadata pills for Peek: collection / type pill.
 *
 * @param {object|null} node
 * @returns {object[]}
 */
export default function usePeekLeadingPills(node) {
  const { theme } = useTheme();
  const { activeBase } = useBase();
  const { getType, isReady } = useNodeTypes();
  const { openDatabase } = useDatabaseOpenIntent();

  const nodeTypesReady = isReady();

  const resolvedTypeKey = useMemo(() => {
    const typeKey = typeof node?.type === 'string' ? node.type.trim() : '';
    return typeKey || '';
  }, [node?.type]);

  const nodeType = useMemo(() => {
    if (!nodeTypesReady || !resolvedTypeKey) return null;
    return getType(resolvedTypeKey) || null;
  }, [getType, nodeTypesReady, resolvedTypeKey]);

  const databasePillLabel = useMemo(() => (
    getNodeTypeLabel(getType, resolvedTypeKey, {
      fallback: node?.type || 'Database',
    })
  ), [getType, node?.type, resolvedTypeKey]);

  const handleDatabasePillClick = useCallback(() => {
    const baseKey = node?.baseKey || activeBase?.key || '';
    const typeKey = resolvedTypeKey || '';
    if (!baseKey || !typeKey) return;

    const databaseKey = `${baseKey}_${typeKey}_all`.replace(/\//g, '_');

    openDatabase(databaseKey, {
      fetch: true,
      typeKey,
      baseKey,
      title: nodeType?.labelPlural || typeKey,
    });
  }, [
    activeBase?.key,
    node?.baseKey,
    nodeType?.labelPlural,
    openDatabase,
    resolvedTypeKey,
  ]);

  return useMemo(() => {
    const pills = [];

    if (resolvedTypeKey) {
      pills.push({
        key: `database-${resolvedTypeKey}`,
        label: databasePillLabel,
        iconKey: nodeType?.iconKey || node?.iconKey || '',
        color: theme.outline,
        labelColor: theme.foreground,
        clickable: true,
        onClick: handleDatabasePillClick,
        className: 'peek-panel-database-pill',
      });
    }

    return pills;
  }, [
    databasePillLabel,
    handleDatabasePillClick,
    node?.iconKey,
    nodeType?.iconKey,
    resolvedTypeKey,
    theme.foreground,
    theme.outline,
  ]);
}
